import AsyncStorage from "@react-native-async-storage/async-storage";
import { captureException } from "@sentry/react-native";
import { atom } from "jotai";
import { Platform } from "react-native";

import { SupportedLanguage } from "@whoosh/design-system/src/components/controls";

export const LANGUAGE_PREFERENCE_STORAGE_KEY = "x-whoosh-language";

export const normalizeLanguage = (
  language?: string | null
): SupportedLanguage | null =>
  language === "en" || language === "fr" ? language : null;

const languageStorage = {
  getItem: async (key: string): Promise<SupportedLanguage | null> => {
    const language =
      Platform.OS === "web"
        ? localStorage.getItem(key)
        : await AsyncStorage.getItem(key);

    return normalizeLanguage(language);
  },
  removeItem: async (key: string): Promise<void> => {
    if (Platform.OS === "web") {
      localStorage.removeItem(key);
      return;
    }

    await AsyncStorage.removeItem(key);
  },
  setItem: async (
    key: string,
    language: SupportedLanguage | null
  ): Promise<void> => {
    if (language === null) {
      await languageStorage.removeItem(key);
      return;
    }

    if (Platform.OS === "web") {
      localStorage.setItem(key, language);
      return;
    }

    await AsyncStorage.setItem(key, language);
  },
};

const storedLanguagePreferenceValueAtom = atom<SupportedLanguage | null>(null);
const storedLanguageWasSelectedAtom = atom(false);
export const storedLanguagePreferenceHydratedAtom = atom(false);

export const storedLanguagePreferenceAtom = atom(
  (get) => get(storedLanguagePreferenceValueAtom),
  async (get, set, language: SupportedLanguage | null) => {
    const previousLanguage = get(storedLanguagePreferenceValueAtom);

    set(storedLanguageWasSelectedAtom, true);
    set(storedLanguagePreferenceValueAtom, language);

    try {
      await languageStorage.setItem(LANGUAGE_PREFERENCE_STORAGE_KEY, language);
    } catch (error) {
      captureException(error);

      if (get(storedLanguagePreferenceValueAtom) === language) {
        set(storedLanguagePreferenceValueAtom, previousLanguage);
      }
    }
  }
);

export const hydrateStoredLanguagePreferenceAtom = atom(
  null,
  async (get, set) => {
    try {
      const language = await languageStorage.getItem(
        LANGUAGE_PREFERENCE_STORAGE_KEY
      );

      if (!get(storedLanguageWasSelectedAtom)) {
        set(storedLanguagePreferenceValueAtom, language);
      }
    } finally {
      set(storedLanguagePreferenceHydratedAtom, true);
    }
  }
);

type ActiveClubLanguageSource = {
  id: symbol;
  language: SupportedLanguage;
};

const activeClubLanguageSourcesAtom = atom<ActiveClubLanguageSource[]>([]);

export const setActiveClubLanguageSourceAtom = atom(
  null,
  (get, set, source: { id: symbol; language: SupportedLanguage | null }) => {
    const otherSources = get(activeClubLanguageSourcesAtom).filter(
      ({ id }) => id !== source.id
    );

    set(
      activeClubLanguageSourcesAtom,
      source.language
        ? [...otherSources, { id: source.id, language: source.language }]
        : otherSources
    );
  }
);

export const activeClubLanguageAtom = atom<SupportedLanguage | null>((get) => {
  const sources = get(activeClubLanguageSourcesAtom);

  return sources[sources.length - 1]?.language ?? null;
});
export const authenticatedLanguagePreferenceAtom =
  atom<SupportedLanguage | null>(null);

export const effectiveLanguageAtom = atom<SupportedLanguage>((get) => {
  return (
    get(authenticatedLanguagePreferenceAtom) ??
    get(storedLanguagePreferenceAtom) ??
    get(activeClubLanguageAtom) ??
    "en"
  );
});
